import React, { useEffect, useRef, useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import {
  NavigationArrow, CaretDown, X, MapPin, Check, AppleLogo, GoogleLogo, Compass,
  Gear, MapPinLine, ArrowsClockwise, SignOut, CheckCircle, Warning, ListNumbers,
  DotsSixVertical, Clock, FloppyDisk, Receipt, ClockAfternoon, Scales, Timer,
} from "@phosphor-icons/react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useAuth } from "../../AuthContext";
import {
  checkLocationPermission,
  getCurrentPosition as driverGetCurrentPosition,
  DriverGeoError,
} from "../../lib/driverGeo";

/**
 * Navigation provider definitions.
 *
 * `url(stop)` returns the deep link to launch the provider with directions
 * to the given stop. Apple Maps uses the `maps://` URL scheme on iOS/macOS
 * (auto-handled by the OS), Waze uses its `waze.com/ul` universal link, and
 * Google Maps uses the standard /maps/dir/?api=1 link that works everywhere.
 */
export const NAV_PROVIDERS = [
  {
    key: "google",
    label: "Google Maps",
    short: "Google",
    Icon: GoogleLogo,
    color: "#4285F4",
    url(stop) {
      const dest = (stop.lat && stop.lng) ? `${stop.lat},${stop.lng}` : encodeURIComponent(stop.service_address || "");
      return `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`;
    },
  },
  {
    key: "apple",
    label: "Apple Maps",
    short: "Apple",
    Icon: AppleLogo,
    color: "#1E1E1E",
    url(stop) {
      const dest = (stop.lat && stop.lng)
        ? `${stop.lat},${stop.lng}`
        : encodeURIComponent(stop.service_address || "");
      // Apple Maps web URL works on every platform — iOS/macOS open the
      // native Maps app automatically, other platforms see the web map.
      return `https://maps.apple.com/?daddr=${dest}&dirflg=d`;
    },
  },
  {
    key: "waze",
    label: "Waze",
    short: "Waze",
    Icon: Compass,
    color: "#00C7D6",
    url(stop) {
      // Waze prefers coordinates. Fall back to a search query if no GPS pin.
      if (stop.lat && stop.lng) {
        return `https://waze.com/ul?ll=${stop.lat},${stop.lng}&navigate=yes`;
      }
      return `https://waze.com/ul?q=${encodeURIComponent(stop.service_address || "")}&navigate=yes`;
    },
  },
];

export const NAV_PROVIDER_BY_KEY = Object.fromEntries(NAV_PROVIDERS.map((p) => [p.key, p]));

/** Launches the provider for the given stop. Opens in a new tab/window so the
 *  driver hub stays loaded behind. On mobile the OS handles the deep-link. */
export function launchNavigation(providerKey, stop) {
  const meta = NAV_PROVIDER_BY_KEY[providerKey] || NAV_PROVIDER_BY_KEY.google;
  if (!stop) return;
  const url = meta.url(stop);
  try {
    window.open(url, "_blank", "noopener");
  } catch {
    window.location.href = url;
  }
}

/** Persistent driver-wide nav preference. Synced to server + localStorage. */
export function useNavPreference() {
  const [pref, setPref] = useState(() => {
    try {
      const raw = localStorage.getItem("zmex.driver.nav_preference");
      if (raw && ["ask", "google", "apple", "waze"].includes(raw)) return raw;
    } catch { /* noop */ }
    return "ask";
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/me/navigation-preference");
        if (cancelled) return;
        const v = data?.nav_preference || "ask";
        setPref(v);
        try { localStorage.setItem("zmex.driver.nav_preference", v); } catch { /* noop */ }
      } catch { /* offline — keep cached */ }
    })();
    return () => { cancelled = true; };
  }, []);

  async function update(next) {
    setPref(next);
    try { localStorage.setItem("zmex.driver.nav_preference", next); } catch { /* noop */ }
    try {
      await api.put("/me/navigation-preference", { nav_preference: next });
    } catch (err) {
      toast.error("Couldn't save preference to server (cached locally)");
    }
  }

  return [pref, update];
}

/**
 * Modal/picker shown when the driver hasn't picked a default — lets them pick
 * a provider for the current stop and optionally remember the choice.
 */
export function NavPickerSheet({ open, stop, onClose, onPick, onRemember, currentPreference }) {
  const [remember, setRemember] = useState(false);
  if (!open || !stop) return null;
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 grid place-items-end sm:place-items-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="nav-picker-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0f1622] text-white w-full sm:max-w-md rounded-t-sm sm:rounded-sm border border-white/10"
      >
        <header className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#CFAB7A] font-bold">Open in</div>
            <h3 className="font-heading text-lg font-black truncate max-w-[260px]">{stop.business_name}</h3>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="nav-picker-close" className="p-2 rounded-sm hover:bg-white/10">
            <X size={18} weight="bold" />
          </button>
        </header>

        <div className="p-5 space-y-2">
          {NAV_PROVIDERS.map((p) => {
            const Icon = p.Icon;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => {
                  if (remember) onRemember(p.key);
                  onPick(p.key);
                }}
                className="w-full inline-flex items-center gap-3 px-4 py-3 rounded-sm border-2 border-white/15 bg-white/5 hover:bg-white/10 hover:border-[#CFAB7A] text-left transition"
                data-testid={`nav-picker-${p.key}`}
              >
                <span className="w-9 h-9 rounded-sm grid place-items-center text-white" style={{ background: p.color }}>
                  <Icon size={18} weight="bold" />
                </span>
                <span className="font-heading font-bold flex-1">{p.label}</span>
                <NavigationArrow size={16} weight="bold" className="text-[#CFAB7A]" />
              </button>
            );
          })}

          <label className="mt-3 flex items-center gap-2 px-1 cursor-pointer select-none" data-testid="nav-picker-remember-label">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="w-4 h-4 accent-[#CFAB7A]"
              data-testid="nav-picker-remember"
            />
            <span className="text-sm text-white/85">Remember my choice (set as default)</span>
          </label>
          {currentPreference && currentPreference !== "ask" && (
            <div className="text-[11px] text-white/55 mt-1 px-1">
              Current default: <span className="font-bold text-[#CFAB7A]">{NAV_PROVIDER_BY_KEY[currentPreference]?.label || currentPreference}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Navigate button + override caret. Drop-in replacement for any "navigate"
 * action button in the driver app. Handles default-vs-ask logic and exposes
 * a small chevron that always shows the provider picker for one-off overrides.
 *
 *   stop:        the stop object with { business_name, service_address, lat, lng }
 *   variant:     "compact" (small inline) | "tablet" (big stack used in TabletDriverHub)
 *   className:   extra classes for the primary button
 */
export function NavigateButton({ stop, variant = "compact", className = "" }) {
  const [pref, setPref] = useNavPreference();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const overrideRef = useRef(null);

  // Close the override menu when clicking outside.
  useEffect(() => {
    if (!overrideOpen) return undefined;
    function onDocClick(e) {
      if (!overrideRef.current?.contains(e.target)) setOverrideOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [overrideOpen]);

  function primaryClick() {
    if (!stop) return;
    if (pref && pref !== "ask") {
      launchNavigation(pref, stop);
    } else {
      setPickerOpen(true);
    }
  }
  function pickerPick(providerKey) {
    launchNavigation(providerKey, stop);
    setPickerOpen(false);
  }

  const activeProvider = pref !== "ask" ? NAV_PROVIDER_BY_KEY[pref] : null;
  const buttonLabel = activeProvider ? `Navigate · ${activeProvider.short}` : "Navigate";

  if (variant === "tablet") {
    // Big stacked button used inside TabletDriverHub. Override caret sits in the corner.
    return (
      <>
        <div className="relative inline-flex flex-col w-full">
          <button
            onClick={primaryClick}
            disabled={!stop}
            className={`inline-flex flex-col items-center justify-center gap-1 rounded-sm bg-white/5 border border-white/15 text-white px-3 py-5 font-bold text-sm hover:bg-white/10 disabled:opacity-40 ${className}`}
            data-testid="hub-navigate-btn"
            title={activeProvider ? `Launches ${activeProvider.label}` : "Pick a navigation app"}
          >
            <NavigationArrow size={22} weight="bold" />
            <span>{activeProvider ? activeProvider.short : "Navigate"}</span>
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOverrideOpen((v) => !v); }}
            ref={overrideRef}
            className="absolute top-1 right-1 p-1 rounded-sm bg-black/30 text-white/70 hover:text-white"
            aria-label="Open in another app"
            data-testid="hub-navigate-override"
            title="Open in another app"
          >
            <CaretDown size={12} weight="bold" />
          </button>
          {overrideOpen && (
            <OverrideMenu
              stop={stop}
              currentKey={pref}
              onPick={(key) => { launchNavigation(key, stop); setOverrideOpen(false); }}
              onClose={() => setOverrideOpen(false)}
            />
          )}
        </div>

        <NavPickerSheet
          open={pickerOpen}
          stop={stop}
          onClose={() => setPickerOpen(false)}
          onPick={pickerPick}
          onRemember={(k) => setPref(k)}
          currentPreference={pref}
        />
      </>
    );
  }

  // Compact (phone) — two adjacent buttons: primary + caret override
  return (
    <>
      <div className="inline-flex items-stretch">
        <button
          onClick={primaryClick}
          disabled={!stop}
          className={`flex-1 inline-flex items-center justify-center gap-2 rounded-l-sm bg-[#CFAB7A] text-[#012340] px-4 py-3 font-bold text-sm disabled:opacity-50 ${className}`}
          data-testid="navigate-btn"
          title={activeProvider ? `Launches ${activeProvider.label}` : "Pick a navigation app"}
        >
          <NavigationArrow size={16} weight="bold" /> {buttonLabel}
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOverrideOpen((v) => !v); }}
          ref={overrideRef}
          className="px-2 rounded-r-sm bg-[#B8946A] text-[#012340] border-l border-[#012340]/20 hover:brightness-105"
          aria-label="Open in another app"
          data-testid="navigate-override-btn"
          title="Open in another app"
        >
          <CaretDown size={14} weight="bold" />
        </button>
        {overrideOpen && (
          <OverrideMenu
            stop={stop}
            currentKey={pref}
            onPick={(key) => { launchNavigation(key, stop); setOverrideOpen(false); }}
            onClose={() => setOverrideOpen(false)}
            alignment="phone"
          />
        )}
      </div>

      <NavPickerSheet
        open={pickerOpen}
        stop={stop}
        onClose={() => setPickerOpen(false)}
        onPick={pickerPick}
        onRemember={(k) => setPref(k)}
        currentPreference={pref}
      />
    </>
  );
}

function OverrideMenu({ stop, currentKey, onPick, onClose, alignment = "tablet" }) {
  return (
    <div
      className={`absolute z-[55] mt-1 w-44 bg-[#0f1622] text-white border border-white/15 rounded-sm shadow-lg ${
        alignment === "phone" ? "right-0 top-full" : "right-0 top-full"
      }`}
      onClick={(e) => e.stopPropagation()}
      data-testid="nav-override-menu"
    >
      <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.25em] text-[#CFAB7A] font-bold">Open with</span>
        <button onClick={onClose} aria-label="Close override" className="p-1 hover:bg-white/10 rounded-sm">
          <X size={12} weight="bold" />
        </button>
      </div>
      {NAV_PROVIDERS.map((p) => {
        const Icon = p.Icon;
        const active = p.key === currentKey;
        return (
          <button
            key={p.key}
            type="button"
            onClick={() => onPick(p.key)}
            className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-white/5 ${active ? "bg-[#CFAB7A]/15" : ""}`}
            data-testid={`nav-override-${p.key}`}
          >
            <span className="w-6 h-6 grid place-items-center rounded-sm text-white" style={{ background: p.color }}>
              <Icon size={12} weight="bold" />
            </span>
            <span className="flex-1 truncate">{p.label}</span>
            {active && <Check size={12} weight="bold" className="text-[#CFAB7A]" />}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Driver Settings sheet — nav preference, app version, GPS controls, updates,
 * refresh, and logout. Opened from the hub via the Gear/Settings button.
 */
export function DriverSettingsSheet({ open, onClose, route, stops, completions, onReloaded }) {
  const [pref, setPref] = useNavPreference();
  const { logout } = useAuth();
  // Sub-sheet: drag-and-drop manual reorder. Rendered as a second overlay
  // on top of Settings so the driver can still tap "Cancel" and return.
  const [manualOpen, setManualOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── Location / GPS state ────────────────────────────────────────
  const [permState, setPermState] = useState("unknown");
  const [gpsStatus, setGpsStatus] = useState({ state: "idle", msg: "" });

  async function refreshPerm() {
    const r = await checkLocationPermission();
    setPermState(r.state || "unknown");
  }
  useEffect(() => { if (open) refreshPerm(); }, [open]);

  async function testGps() {
    setGpsStatus({ state: "loading", msg: "Getting current position…" });
    try {
      const p = await driverGetCurrentPosition({ timeout: 15000 });
      setGpsStatus({
        state: "ok",
        msg: `${p.coords.latitude.toFixed(6)}, ${p.coords.longitude.toFixed(6)} (±${Math.round(p.coords.accuracy || 0)}m)`,
      });
      refreshPerm();
    } catch (e) {
      let msg = e?.message || "Location error";
      if (e instanceof DriverGeoError && e.code === "PERMISSION_DENIED") {
        msg = "Location permission is blocked. Enable location for this site in your browser settings.";
      }
      setGpsStatus({ state: "err", msg });
      refreshPerm();
    }
  }

  function reloadApp() {
    try { window.location.reload(); } catch { /* ignore */ }
  }

  // ── Route sequence handlers ─────────────────────────────────────
  // "Save Today's Order" computes the new stop_ids from the actual completion
  // order today: completed / issue / acknowledged stops first, ordered by
  // `completed_at`; then everything that wasn't touched, preserving current
  // admin-set order. That new sequence is persisted so tomorrow's route runs
  // in the same order the driver actually drove today.
  async function saveTodayOrder() {
    if (!route?.id) { toast.error("No active route"); return; }
    if (!stops?.length) { toast.error("No stops on this route"); return; }
    if (!completions?.length) {
      toast.error("Complete at least one stop before saving today's order");
      return;
    }
    // stopKey → earliest completed_at ISO timestamp for that stop.
    const stopCompletedAt = {};
    for (const c of completions) {
      const key = c.container_id || c.customer_id;
      const at  = c.completed_at;
      if (!key || !at) continue;
      if (!stopCompletedAt[key] || at < stopCompletedAt[key]) stopCompletedAt[key] = at;
    }
    // Preserve the caller's current sequence for anything not completed.
    const currentIds = (route.stop_ids && route.stop_ids.length)
      ? route.stop_ids
      : stops.map((s) => s.container_id || s.customer_id || s.id);
    const withOrder = currentIds.map((id, i) => ({ id, at: stopCompletedAt[id], i }));
    withOrder.sort((a, b) => {
      if (a.at && b.at) return a.at.localeCompare(b.at);
      if (a.at) return -1;
      if (b.at) return 1;
      return a.i - b.i;
    });
    const newIds = withOrder.map((x) => x.id);
    setSaving(true);
    try {
      await api.post(`/driver/routes/${route.id}/reorder`, { stop_ids: newIds });
      toast.success(`Route saved in today's completion order (${completions.length} stops)`);
      onReloaded && await onReloaded();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save today's order");
    } finally {
      setSaving(false);
    }
  }

  function doLogout() {
    logout?.();
    onClose?.();
  }

  if (!open) return null;
  const opts = [
    { key: "ask", label: "Ask Every Time", color: "#5C738A", Icon: MapPin },
    ...NAV_PROVIDERS.map((p) => ({ key: p.key, label: p.label, color: p.color, Icon: p.Icon })),
  ];

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 grid place-items-end sm:place-items-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="driver-settings-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0f1622] text-white w-full sm:max-w-xl rounded-t-sm sm:rounded-sm border border-white/10 max-h-[92vh] overflow-y-auto"
      >
        <header className="px-5 py-4 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[#0f1622] z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#CFAB7A] font-bold">Driver</div>
            <h3 className="font-heading text-xl font-black">Settings</h3>
          </div>
          <button onClick={onClose} aria-label="Close settings" data-testid="driver-settings-close" className="p-2 rounded-sm hover:bg-white/10">
            <X size={18} weight="bold" />
          </button>
        </header>

        {/* ─── Location / GPS ────────────────────────────────────── */}
        <Section title="Location / GPS" icon={MapPinLine} testid="settings-section-gps">
          <div className="text-xs text-white/70 mb-2">
            Current permission:{" "}
            <span className="font-mono text-white" data-testid="settings-gps-state">
              {permState}
            </span>
          </div>
          {gpsStatus.state === "ok" && <Note tone="ok" icon={CheckCircle}>Got location: {gpsStatus.msg}</Note>}
          {gpsStatus.state === "err" && <Note tone="err" icon={Warning}>{gpsStatus.msg}</Note>}
          {gpsStatus.state === "loading" && <Note tone="info">{gpsStatus.msg}</Note>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
            <SettingsButton onClick={testGps} icon={MapPin} testid="settings-gps-test">
              Test / Grant Location
            </SettingsButton>
          </div>
          <div className="text-[11px] text-white/50 mt-2">
            If location is blocked, enable it for this site in your browser's site settings.
          </div>
        </Section>

        {/* ─── Navigation Preference ─────────────────────────────── */}
        <Section title="Navigation Preference" icon={NavigationArrow} testid="settings-section-nav">
          <p className="text-xs text-white/65 mb-3">
            Pick the app that opens when you tap Navigate. Choose &ldquo;Ask Every Time&rdquo; to keep the picker.
          </p>
          <div className="space-y-2">
            {opts.map((o) => {
              const Icon = o.Icon;
              const active = pref === o.key;
              return (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setPref(o.key)}
                  className={`w-full inline-flex items-center gap-3 px-4 py-3 rounded-sm border-2 transition ${active ? "bg-[#CFAB7A] text-[#012340] border-[#CFAB7A]" : "bg-white/5 text-white border-white/15 hover:border-white/30"}`}
                  data-testid={`nav-pref-${o.key}`}
                >
                  <span className="w-8 h-8 rounded-sm grid place-items-center text-white flex-shrink-0" style={{ background: active ? "#012340" : o.color }}>
                    <Icon size={16} weight="bold" />
                  </span>
                  <span className="font-heading font-bold flex-1 text-left">{o.label}</span>
                  {active && <Check size={16} weight="bold" />}
                </button>
              );
            })}
          </div>
        </Section>

        {/* ─── Route Sequence ───────────────────────────────────── */}
        <Section title="Route Sequence" icon={ListNumbers} testid="settings-section-route-order">
          <p className="text-xs text-white/65 mb-3">
            Reorder today's stops so tomorrow's route runs the same way you did today.
            Or drag stops manually to lock in your preferred sequence.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <SettingsButton
              onClick={saveTodayOrder}
              icon={Clock}
              testid="settings-save-today-order"
              disabled={saving || !route?.id || !completions?.length}
            >
              {saving ? "Saving…" : "Save Today's Order"}
            </SettingsButton>
            <SettingsButton
              onClick={() => setManualOpen(true)}
              icon={DotsSixVertical}
              testid="settings-manual-reorder"
              disabled={!route?.id || !stops?.length}
            >
              Manual Reorder
            </SettingsButton>
          </div>
          <div className="text-[11px] text-white/50 mt-2">
            {completions?.length
              ? `${completions.length} of ${stops?.length || 0} stops touched today.`
              : "Complete at least one stop before saving today's order."}
          </div>
        </Section>

        {/* ─── App controls ──────────────────────────────────────── */}
        <Section title="App" icon={Gear} testid="settings-section-app">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <SettingsButton onClick={reloadApp} icon={ArrowsClockwise} testid="settings-reload">
              Refresh / Reload
            </SettingsButton>
            <SettingsButton onClick={doLogout} icon={SignOut} testid="settings-logout" tone="danger">
              Logout
            </SettingsButton>
          </div>
        </Section>

        <footer className="px-5 py-3 border-t border-white/10 text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold flex items-center justify-between">
          <span>ZMEX Driver</span>
          <span>com.zmexdisposal.driver</span>
        </footer>
      </div>

      {manualOpen && (
        <ManualReorderSheet
          route={route}
          stops={stops}
          completions={completions}
          onClose={() => setManualOpen(false)}
          onSaved={async () => { setManualOpen(false); onReloaded && await onReloaded(); onClose?.(); }}
        />
      )}
    </div>
  );
}

function Section({ title, icon: Icon, children, testid }) {
  return (
    <section className="px-5 py-4 border-b border-white/5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} weight="bold" className="text-[#CFAB7A]" />
        <h4 className="text-[10px] uppercase tracking-[0.3em] text-[#CFAB7A] font-bold">{title}</h4>
      </div>
      {children}
    </section>
  );
}

function SettingsButton({ onClick, icon: Icon, children, tone = "default", testid, disabled }) {
  const cls = tone === "danger"
    ? "bg-[#D32F2F]/15 border-[#D32F2F]/40 text-[#FF8A80] hover:bg-[#D32F2F]/25"
    : "bg-white/5 border-white/15 text-white hover:bg-white/10 hover:border-white/30";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm border-2 text-sm font-heading font-bold transition disabled:opacity-40 disabled:cursor-not-allowed ${cls}`}
    >
      {Icon && <Icon size={16} weight="bold" />}
      {children}
    </button>
  );
}

function Note({ tone = "info", icon: Icon, children }) {
  const cls = {
    ok:   "border-[#2E7D32]/40 bg-[#2E7D32]/10 text-[#7FC683]",
    err:  "border-[#D32F2F]/40 bg-[#D32F2F]/10 text-[#FF8A80]",
    info: "border-white/15 bg-white/5 text-white/85",
  }[tone];
  return (
    <div className={`text-xs rounded-sm border p-2 flex items-start gap-2 ${cls}`}>
      {Icon && <Icon size={14} weight="bold" className="flex-shrink-0 mt-0.5" />}
      <span>{children}</span>
    </div>
  );
}

/**
 * ManualReorderSheet — drag-and-drop route sequence editor.
 *
 * Local drag state lets the driver rearrange freely and only commits to the
 * server when they hit "Save Order". Cancel discards. Completed stops render
 * with a green tint + ✓ badge so the driver can see what they already ran.
 *
 * The stop id used in the drag list is the same key used everywhere else —
 * container_id if the stop is container-scoped, otherwise customer_id / id.
 * This matches how `route.stop_ids` is stored on the backend, so what we
 * POST back is what the reorder endpoint expects.
 */
function stopKey(s) { return s.container_id || s.customer_id || s.id; }

function ManualReorderSheet({ route, stops, completions, onClose, onSaved }) {
  // Build the initial ordered array from the ROUTE's stop_ids so we honor
  // the current admin sequence, not whatever nearest-stop sorting the map
  // may have applied on the parent. Fall back to raw stops otherwise.
  const currentIds = (route?.stop_ids && route.stop_ids.length)
    ? route.stop_ids
    : (stops || []).map(stopKey);
  const stopById = React.useMemo(() => {
    const m = {};
    for (const s of stops || []) m[stopKey(s)] = s;
    return m;
  }, [stops]);
  const [orderedIds, setOrderedIds] = useState(
    currentIds.filter((id) => stopById[id])
  );
  const [saving, setSaving] = useState(false);

  const completedSet = React.useMemo(() => {
    const set = new Set();
    for (const c of completions || []) {
      const key = c.container_id || c.customer_id;
      if (key) set.add(key);
    }
    return set;
  }, [completions]);

  function onDragEnd(result) {
    if (!result.destination) return;
    const src = result.source.index;
    const dst = result.destination.index;
    if (src === dst) return;
    setOrderedIds((prev) => {
      const next = prev.slice();
      const [moved] = next.splice(src, 1);
      next.splice(dst, 0, moved);
      return next;
    });
  }

  async function save() {
    if (!route?.id) return;
    setSaving(true);
    try {
      await api.post(`/driver/routes/${route.id}/reorder`, { stop_ids: orderedIds });
      toast.success(`Route order saved (${orderedIds.length} stops)`);
      onSaved && await onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the new order");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/85 grid place-items-end sm:place-items-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="manual-reorder-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0f1622] text-white w-full sm:max-w-lg rounded-t-sm sm:rounded-sm border border-white/10 max-h-[92vh] flex flex-col"
      >
        <header className="px-5 py-4 border-b border-white/10 flex items-center justify-between flex-shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#CFAB7A] font-bold">Route Sequence</div>
            <h3 className="font-heading text-xl font-black">Drag to reorder</h3>
          </div>
          <button
            onClick={onClose}
            aria-label="Close reorder"
            data-testid="manual-reorder-close"
            className="p-2 rounded-sm hover:bg-white/10"
          >
            <X size={18} weight="bold" />
          </button>
        </header>
        <div className="px-5 pt-3 text-[11px] text-white/60">
          Drag the ⋮⋮ handle to move a stop. Completed stops show a green check —
          they can be reordered too if you want to lock a repeatable sequence.
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="manual-reorder-list">
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="stops">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                  {orderedIds.map((id, index) => {
                    const s = stopById[id];
                    if (!s) return null;
                    const isDone = completedSet.has(id);
                    return (
                      <Draggable key={id} draggableId={id} index={index}>
                        {(prov, snapshot) => (
                          <div
                            ref={prov.innerRef}
                            {...prov.draggableProps}
                            className={`flex items-center gap-3 rounded-sm border p-3 select-none ${
                              snapshot.isDragging ? "bg-[#CFAB7A]/25 border-[#CFAB7A]" :
                              isDone              ? "bg-[#2E7D32]/10 border-[#2E7D32]/40" :
                                                    "bg-[#0a1422] border-white/10"
                            }`}
                            data-testid={`manual-reorder-row-${id}`}
                          >
                            <div
                              {...prov.dragHandleProps}
                              className="cursor-grab active:cursor-grabbing text-white/40 hover:text-white p-1"
                              aria-label="Drag handle"
                            >
                              <DotsSixVertical size={22} weight="bold" />
                            </div>
                            <div className={`w-8 h-8 rounded-sm grid place-items-center text-sm font-black flex-shrink-0 ${
                              isDone ? "bg-[#2E7D32] text-white" : "bg-[#CFAB7A] text-[#012340]"
                            }`}>
                              {isDone ? "✓" : index + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-bold truncate">{s.business_name}</div>
                              <div className="text-[11px] text-white/55 truncate">{s.service_address}</div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>

        <footer className="p-4 border-t border-white/10 flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm border-2 border-white/15 text-white/80 hover:bg-white/5 font-heading font-bold text-sm"
            data-testid="manual-reorder-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || orderedIds.length === 0}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm bg-[#CFAB7A] text-[#012340] hover:brightness-105 disabled:opacity-40 font-heading font-black text-sm"
            data-testid="manual-reorder-save"
          >
            <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save Order"}
          </button>
        </footer>
      </div>
    </div>
  );
}

/**
 * DriverDisposalSheet — driver-facing disposal ticket submission.
 *
 * Presented as a bottom sheet from the "Disposal" action button. Captures:
 *   * Disposal site name (facility) — for now free-text; later a picker.
 *   * Time In (tap on arrival)  / Time Out (tap on departure).
 *   * All ticket fields the admin dashboard exposes: dump date, tons, price/ton,
 *     total-cost override, ticket #, gross/tare weights, waste stream, notes.
 *
 * On submit, POSTs to /api/disposal-tickets/driver — the record lands in the
 * SAME collection as admin tickets so it shows up on the dashboard immediately.
 */
function nowLocalIso() {
  // Local timezone-aware ISO string (no seconds trimming — Mongo will store as-is).
  const d = new Date();
  const off = -d.getTimezoneOffset();
  const sign = off >= 0 ? "+" : "-";
  const pad = (n) => String(Math.abs(n)).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}${sign}${pad(off / 60)}:${pad(off % 60)}`;
}
function fmtClock(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch { return "—"; }
}
function todayIsoDate() { return new Date().toISOString().slice(0, 10); }

export function DriverDisposalSheet({ open, onClose, route, onSaved }) {
  const empty = {
    facility: "",
    dump_date: todayIsoDate(),
    time_in: null,
    time_out: null,
    tons: "",
    price_per_ton: "",
    total_cost: "",
    ticket_number: "",
    gross_weight_lb: "",
    tare_weight_lb: "",
    waste_stream: "MSW",
    notes: "",
    overrideTotal: false,
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(empty); /* eslint-disable-next-line */ }, [open]);

  // Auto-compute tons from (gross - tare)/2000 when both are provided AND
  // the driver hasn't manually typed a tons value. This just fills the field;
  // driver can still edit.
  useEffect(() => {
    const g = parseFloat(form.gross_weight_lb);
    const t = parseFloat(form.tare_weight_lb);
    if (Number.isFinite(g) && Number.isFinite(t) && g > t) {
      const tons = ((g - t) / 2000).toFixed(2);
      // Only overwrite if the current tons field matches a prior auto-value
      // or is empty. Never clobber a manual entry.
      setForm((prev) => (prev.tons === "" || prev._tonsAuto === true)
        ? { ...prev, tons, _tonsAuto: true }
        : prev);
    }
    // eslint-disable-next-line
  }, [form.gross_weight_lb, form.tare_weight_lb]);

  const autoTotal = (() => {
    const t = parseFloat(form.tons);
    const p = parseFloat(form.price_per_ton);
    if (Number.isFinite(t) && Number.isFinite(p)) return Math.round(t * p * 100) / 100;
    return 0;
  })();

  if (!open) return null;

  async function submit(e) {
    e?.preventDefault?.();
    if (!form.facility.trim()) { toast.error("Enter the disposal site name"); return; }
    if (!form.dump_date) { toast.error("Dump date required"); return; }
    if (!form.tons || Number(form.tons) <= 0) { toast.error("Tons must be > 0"); return; }
    if (form.price_per_ton === "" || Number(form.price_per_ton) < 0) { toast.error("Price / Ton is required"); return; }
    setSaving(true);
    try {
      const body = {
        facility: form.facility.trim(),
        dump_date: form.dump_date,
        tons: Number(form.tons),
        price_per_ton: Number(form.price_per_ton),
        total_cost: form.overrideTotal ? Number(form.total_cost) : undefined,
        ticket_number: form.ticket_number.trim() || undefined,
        time_in: form.time_in || undefined,
        time_out: form.time_out || undefined,
        gross_weight_lb: form.gross_weight_lb ? Number(form.gross_weight_lb) : undefined,
        tare_weight_lb:  form.tare_weight_lb  ? Number(form.tare_weight_lb)  : undefined,
        waste_stream: form.waste_stream || undefined,
        notes: form.notes.trim() || undefined,
        route_id: route?.id || undefined,
      };
      await api.post("/disposal-tickets/driver", body);
      onSaved && await onSaved();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit disposal ticket");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 grid place-items-end sm:place-items-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="driver-disposal-sheet"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-[#0f1622] text-white w-full sm:max-w-xl rounded-t-sm sm:rounded-sm border border-white/10 max-h-[92vh] flex flex-col"
      >
        <header className="px-5 py-4 border-b border-white/10 flex items-center justify-between flex-shrink-0 bg-[#012340]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#CFAB7A] font-bold">Driver</div>
            <h3 className="font-heading text-xl font-black flex items-center gap-2">
              <Receipt size={20} weight="bold" className="text-[#CFAB7A]" /> Disposal Ticket
            </h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Close disposal" data-testid="disposal-close" className="p-2 rounded-sm hover:bg-white/10">
            <X size={18} weight="bold" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Site name — sits prominently at the top per spec */}
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#CFAB7A] font-bold mb-1 flex items-center gap-1.5">
              <MapPin size={12} weight="bold" /> Disposal Site Name
            </div>
            <input
              type="text"
              value={form.facility}
              onChange={(e) => setForm((f) => ({ ...f, facility: e.target.value }))}
              placeholder="e.g. Central Landfill"
              className="w-full px-3 py-3 bg-[#012340] border-2 border-[#CFAB7A]/40 rounded-sm text-base font-bold placeholder-white/30 focus:border-[#CFAB7A] outline-none"
              data-testid="disposal-facility"
              required
            />
          </label>

          {/* Time In / Time Out */}
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, time_in: f.time_in ? null : nowLocalIso() }))}
              className={`rounded-sm border-2 px-3 py-3 flex flex-col items-center gap-1 transition ${
                form.time_in
                  ? "bg-[#2E7D32]/25 border-[#2E7D32] text-[#7FC683]"
                  : "bg-white/5 border-white/15 text-white hover:border-white/30"
              }`}
              data-testid="disposal-time-in"
            >
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold flex items-center gap-1.5">
                <ClockAfternoon size={13} weight="bold" /> Time In
              </div>
              <div className="font-heading font-black text-lg tabular-nums">
                {form.time_in ? fmtClock(form.time_in) : "Tap on arrival"}
              </div>
            </button>
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, time_out: f.time_out ? null : nowLocalIso() }))}
              className={`rounded-sm border-2 px-3 py-3 flex flex-col items-center gap-1 transition ${
                form.time_out
                  ? "bg-[#CFAB7A]/25 border-[#CFAB7A] text-[#CFAB7A]"
                  : "bg-white/5 border-white/15 text-white hover:border-white/30"
              }`}
              data-testid="disposal-time-out"
            >
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold flex items-center gap-1.5">
                <Timer size={13} weight="bold" /> Time Out
              </div>
              <div className="font-heading font-black text-lg tabular-nums">
                {form.time_out ? fmtClock(form.time_out) : "Tap on departure"}
              </div>
            </button>
          </div>

          {/* Dump date */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-bold mb-1">Dump Date *</div>
              <input
                type="date"
                value={form.dump_date}
                onChange={(e) => setForm((f) => ({ ...f, dump_date: e.target.value }))}
                className="w-full px-3 py-2.5 bg-[#012340] border border-white/15 rounded-sm text-sm text-white focus:border-[#CFAB7A] outline-none"
                data-testid="disposal-date"
                required
              />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-bold mb-1">Ticket #</div>
              <input
                type="text"
                value={form.ticket_number}
                onChange={(e) => setForm((f) => ({ ...f, ticket_number: e.target.value }))}
                placeholder="From the landfill scale"
                className="w-full px-3 py-2.5 bg-[#012340] border border-white/15 rounded-sm text-sm text-white focus:border-[#CFAB7A] outline-none placeholder-white/30"
                data-testid="disposal-ticket-num"
              />
            </label>
          </div>

          {/* Gross / Tare / Tons */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#CFAB7A] font-bold mb-1 flex items-center gap-1.5">
              <Scales size={12} weight="bold" /> Weights
            </div>
            <div className="grid grid-cols-3 gap-2">
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 font-bold mb-1">Gross (lb)</div>
                <input
                  type="number" inputMode="decimal" min="0"
                  value={form.gross_weight_lb}
                  onChange={(e) => setForm((f) => ({ ...f, gross_weight_lb: e.target.value }))}
                  className="w-full px-2.5 py-2.5 bg-[#012340] border border-white/15 rounded-sm text-sm text-white font-mono focus:border-[#CFAB7A] outline-none"
                  data-testid="disposal-gross"
                />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 font-bold mb-1">Tare (lb)</div>
                <input
                  type="number" inputMode="decimal" min="0"
                  value={form.tare_weight_lb}
                  onChange={(e) => setForm((f) => ({ ...f, tare_weight_lb: e.target.value }))}
                  className="w-full px-2.5 py-2.5 bg-[#012340] border border-white/15 rounded-sm text-sm text-white font-mono focus:border-[#CFAB7A] outline-none"
                  data-testid="disposal-tare"
                />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#CFAB7A] font-bold mb-1">Tons *</div>
                <input
                  type="number" inputMode="decimal" min="0" step="0.01"
                  value={form.tons}
                  onChange={(e) => setForm((f) => ({ ...f, tons: e.target.value, _tonsAuto: false }))}
                  className="w-full px-2.5 py-2.5 bg-[#012340] border-2 border-[#CFAB7A]/40 rounded-sm text-sm text-white font-mono focus:border-[#CFAB7A] outline-none"
                  data-testid="disposal-tons"
                  required
                />
              </label>
            </div>
            <div className="text-[10px] text-white/40 mt-1">
              Tons auto-fills from (Gross − Tare) ÷ 2000. Override anytime.
            </div>
          </div>

          {/* Price + total */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-bold mb-1">Price / Ton *</div>
              <input
                type="number" inputMode="decimal" min="0" step="0.01"
                value={form.price_per_ton}
                onChange={(e) => setForm((f) => ({ ...f, price_per_ton: e.target.value }))}
                placeholder="54.20"
                className="w-full px-3 py-2.5 bg-[#012340] border border-white/15 rounded-sm text-sm text-white font-mono focus:border-[#CFAB7A] outline-none placeholder-white/30"
                data-testid="disposal-ppt"
                required
              />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-bold mb-1 flex items-center gap-2">
                Total (${autoTotal.toFixed(2)})
                <label className="ml-auto text-[10px] font-normal cursor-pointer flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={form.overrideTotal}
                    onChange={(e) => setForm((f) => ({ ...f, overrideTotal: e.target.checked, total_cost: e.target.checked ? (f.total_cost || autoTotal) : "" }))}
                    className="accent-[#CFAB7A]"
                    data-testid="disposal-override"
                  />
                  Override
                </label>
              </div>
              <input
                type="number" inputMode="decimal" min="0" step="0.01"
                disabled={!form.overrideTotal}
                value={form.overrideTotal ? form.total_cost : autoTotal}
                onChange={(e) => setForm((f) => ({ ...f, total_cost: e.target.value }))}
                className={`w-full px-3 py-2.5 rounded-sm text-sm text-white font-mono border focus:border-[#CFAB7A] outline-none ${form.overrideTotal ? "bg-[#012340] border-[#CFAB7A]" : "bg-white/5 border-white/10 text-white/50"}`}
                data-testid="disposal-total"
              />
            </label>
          </div>

          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-bold mb-1">Waste Stream</div>
            <select
              value={form.waste_stream}
              onChange={(e) => setForm((f) => ({ ...f, waste_stream: e.target.value }))}
              className="w-full px-3 py-2.5 bg-[#012340] border border-white/15 rounded-sm text-sm text-white focus:border-[#CFAB7A] outline-none"
              data-testid="disposal-stream"
            >
              <option value="MSW">MSW · Municipal Solid Waste</option>
              <option value="C&D">C&amp;D · Construction &amp; Demolition</option>
              <option value="Recycling">Recycling</option>
              <option value="Green">Green Waste</option>
              <option value="Other">Other</option>
            </select>
          </label>

          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-bold mb-1">Notes</div>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={2}
              placeholder="Anything the office should know"
              className="w-full px-3 py-2.5 bg-[#012340] border border-white/15 rounded-sm text-sm text-white focus:border-[#CFAB7A] outline-none placeholder-white/30"
              data-testid="disposal-notes"
            />
          </label>
        </div>

        <footer className="p-4 border-t border-white/10 flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm border-2 border-white/15 text-white/80 hover:bg-white/5 font-heading font-bold text-sm"
            data-testid="disposal-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm bg-[#CFAB7A] text-[#012340] hover:brightness-105 disabled:opacity-40 font-heading font-black text-sm"
            data-testid="disposal-submit"
          >
            <FloppyDisk size={16} weight="bold" /> {saving ? "Submitting…" : "Submit Ticket"}
          </button>
        </footer>
      </form>
    </div>
  );
}
